import * as React from 'react';
import { classNames } from 'utils/ui/css';

export interface IContextMenuProps {
  isActive: boolean;
  isPinned: boolean;
  onToggleActive: () => void;
  onAddTenant: () => void;
  onPinToTop: () => void;
}

const MENU_MIN_SPACE_BELOW = 200;

export const ContextMenu = ({ isActive, isPinned, onToggleActive, onAddTenant, onPinToTop }: IContextMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [opensUpward, setOpensUpward] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = () => {
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleTriggerClick = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setOpensUpward(window.innerHeight - rect.bottom < MENU_MIN_SPACE_BELOW);
    }
    setIsOpen((prev) => !prev);
  };

  const handleToggleActive = () => {
    onToggleActive();
    setIsOpen(false);
  };

  const handleAddTenant = () => {
    onAddTenant();
    setIsOpen(false);
  };

  const handlePinToTop = () => {
    onPinToTop();
    setIsOpen(false);
  };

  /** Prevent mousedown inside the menu from reaching the document listener */
  const stopMenuMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="relative inline-block" onMouseDown={stopMenuMouseDown}>
      <button
        type="button"
        data-testid="context-menu-trigger"
        ref={triggerRef}
        onClick={handleTriggerClick}
        aria-label="Actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
        className="inline-flex items-center justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      >
        <svg className="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="menu"
          className={classNames(
            'absolute right-0 z-20 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5',
            opensUpward ? 'bottom-full mb-1' : 'mt-1'
          )}
        >
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={handlePinToTop}
            >
              {isPinned ? (
                <svg className="size-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.59099 1.00216C9.78625 0.806897 10.1028 0.806897 10.2981 1.00216L15.2478 5.95191C15.4431 6.14717 15.4431 6.46375 15.2478 6.65901L13.8336 8.07323C13.6384 8.26849 13.3218 8.26849 13.1265 8.07323L12.773 7.71967L11.0052 9.48744L10.6813 11.1068C10.662 11.2036 10.6144 11.2925 10.5446 11.3623L9.23744 12.6694C9.04218 12.8647 8.72559 12.8647 8.53033 12.6694L3.58058 7.71967C3.38532 7.52441 3.38532 7.20783 3.58058 7.01257L4.88775 5.7054C4.95755 5.6356 5.04645 5.58802 5.14325 5.56866L6.76256 5.2448L8.53033 3.47703L8.17678 3.12348C7.98152 2.92822 7.98152 2.61164 8.17678 2.41637L9.59099 1.00216Z"
                    fill="#008075"
                  />
                  <path
                    d="M4.9948 10.5481L5.7019 11.2552L3.78261 13.1745L3.22703 13.023L3.07551 12.4674L4.9948 10.5481Z"
                    fill="#008075"
                  />
                </svg>
              ) : (
                <svg
                  className="size-4 text-teal"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75L17.25 9m0 0L21 12.75M17.25 9v12"
                  />
                </svg>
              )}
              {isPinned ? 'Unpin' : 'Pin at the top'}
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={handleAddTenant}
            >
              <svg
                className="size-4 text-teal"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                />
              </svg>
              Add tenant
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={handleToggleActive}
            >
              <svg
                className="size-4 text-teal"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9" />
              </svg>
              {isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
